// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import React from 'react';
import { Chevron } from '../../../lib/animated';
import {
  Box,
  Card,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '../../../lib/base';

interface Column {
  readonly id: string;
  readonly label: string;
  readonly minWidth?: number;
  readonly align?: 'right' | 'left' | 'center';
}

interface Row {
  readonly details: React.ReactNode;
  readonly key: string;
  readonly [key: string]: string | number | boolean | React.ReactNode;
}

interface TableProps {
  readonly columns: Column[];
  readonly rows: Row[];
}

const ExpandableTable: React.FC<TableProps> = ({ columns, rows }) => {
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(new Set());
  const [isAllExpanded, setIsAllExpanded] = React.useState<boolean>(false);

  const handleRowClick = (index: number) => {
    setExpandedRows((prev) => {
      const newExpandedRows = new Set(prev);
      if (newExpandedRows.has(index)) {
        newExpandedRows.delete(index);
      } else {
        newExpandedRows.add(index);
      }
      // Update `isAllExpanded` based on the new state of `expandedRows`
      if (newExpandedRows.size === rows.length) {
        setIsAllExpanded(true);
      } else if (newExpandedRows.size === 0) {
        setIsAllExpanded(false);
      }

      return newExpandedRows;
    });
  };

  const handleExpandAll = () => {
    if (isAllExpanded) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(rows.map((_, index) => index)));
    }
    setIsAllExpanded(!isAllExpanded);
  };

  return (
    <TableContainer component={Card}>
      <Table>
        <TableHead>
          <TableRow onClick={handleExpandAll} sx={{ cursor: 'pointer' }}>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align}
                sx={{
                  // Same as header in Column.tsx
                  minWidth: column.minWidth,
                  fontFamily: 'fontFamily',
                  fontSize: 16,
                  fontWeight: 'fontWeightMedium',
                  color: 'text.secondary',
                  paddingTop: 0,
                  paddingBottom: 0,
                  height: 48,
                }}
              >
                {column.label}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ paddingTop: 0, paddingBottom: 0, height: 48 }}>
              <IconButton onClick={handleExpandAll}>
                <Chevron up={!isAllExpanded} />
              </IconButton>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const isLastRow = index === rows.length - 1;
            const isExpanded = expandedRows.has(index);

            return (
              <React.Fragment key={row.key}>
                <TableRow
                  onClick={() => handleRowClick(index)}
                  sx={{
                    cursor: 'pointer',
                    // backgroundColor: (theme) => (isOddRow ? theme.custom.colors.common.oddRow : undefined),
                  }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{ borderBottom: isLastRow && !isExpanded ? 'none' : undefined }}
                    >
                      {row[column.id]}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      paddingTop: 0,
                      paddingBottom: 0,
                      height: 48,
                      borderBottom: isLastRow && !isExpanded ? 'none' : undefined,
                      width: 0,
                    }}
                  >
                    <IconButton>
                      <Chevron up={!isExpanded} />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    sx={{
                      padding: 0,
                      borderBottom: isLastRow ? 'none' : isExpanded ? undefined : 'none',
                      width: 0,
                    }}
                  >
                    <Collapse in={isExpanded} timeout="auto" sx={{ overflow: 'hidden' }}>
                      <Box
                        sx={{
                          margin: 1,
                        }}
                      >
                        {row.details}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExpandableTable;
